import { Icon } from './Icon';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { BOARD_FLASH_MS, BOARD_LINK, BOARD_NUMBER_SIZE, LANG_DIR, LANG_LABELS, UI, announceReady, fill, readBoardMemory, writeBoardMemory, type Lang } from '@ringring/core';
import { api, subscribeBoard, type ApiError, type BoardSettings, type PublicBoard, type PublicBoardEntry } from './api';
import { boardSound, type SoundState } from './audio';
import { Logo } from './lang';

/** Set once the screen has been started by a tap; the full-screen overlay is not shown again on this browser. */
const STARTED_KEY = 'rr_board_started';
/** The ready numbers this screen has shown, per pickup point, so a reload does not chime for them again. */
const memoryKey = (pickupPointId: string) => `rr_board_seen:${pickupPointId}`;
/**
 * The board link's key this browser last opened successfully, per pickup point (issue #16): a reload whose URL lost
 * its `?key=` (a kiosk that keeps only the path, a bookmark of the bare route) still opens. A refused key is forgotten.
 */
const linkKeyStorage = (pickupPointId: string) => `rr_board_key:${pickupPointId}`;
/**
 * The board settings (venue language, second voice language, venue logo) are polled every `BOARD_LINK.checkEveryMs`
 * (30 s), so a change on the dashboard reaches the open TV within a minute; a `settings` message on the socket (sent
 * after every settings change, and after a logo upload: issue #20) makes the board read them again at once. A 401
 * there is also how a revoked link is noticed when the socket notice and close were missed (issue #16).
 */
const SETTINGS_POLL_MS = BOARD_LINK.checkEveryMs;

/** Whether the board may run: its link was given and not refused, it was opened without a key, or the API refused the key. */
export type BoardLinkState = 'ok' | 'missing' | 'invalid';

/** The key to open the board with: the URL's, else the one this browser last opened successfully. */
export function boardKeyFor(pickupPointId: string, fromUrl: string | null): string | null {
  if (fromUrl) return fromUrl;
  try {
    return localStorage.getItem(linkKeyStorage(pickupPointId));
  } catch {
    return null;
  }
}

function rememberBoardKey(pickupPointId: string, key: string | null): void {
  try {
    if (key) localStorage.setItem(linkKeyStorage(pickupPointId), key);
    else localStorage.removeItem(linkKeyStorage(pickupPointId));
  } catch {
    // storage unavailable: the URL's key still works
  }
}

function readStarted(): boolean {
  try {
    return localStorage.getItem(STARTED_KEY) === '1';
  } catch {
    return false;
  }
}

const numberSize = { '--board-num-size': `clamp(${BOARD_NUMBER_SIZE.minPx}px, ${BOARD_NUMBER_SIZE.vw}vw, ${BOARD_NUMBER_SIZE.maxPx}px)` } as CSSProperties;

/**
 * The TV board of one pickup point, opened from its signed link (issue #16, US-43): `boardKey` is the link's key, sent
 * on the settings request and the socket; without one the screen says a link is needed, and once the API refuses the
 * key (revoked, mistyped) or the socket is closed for revocation it says the link is not valid and stops. `lang` is the
 * device language, used only until the board settings arrive: the screen reads and speaks in the venue's default
 * language (spec, "TV board"), then in the pickup point's optional second language. `settings` can be handed in by a
 * host that already has them (and by the tests), `linkState` too.
 */
export function Board({
  pickupPointId,
  boardKey = null,
  lang,
  settings: initialSettings = null,
  linkState: initialLinkState,
}: {
  pickupPointId: string;
  boardKey?: string | null;
  lang: Lang;
  settings?: BoardSettings | null;
  linkState?: BoardLinkState;
}) {
  const [settings, setSettings] = useState<BoardSettings | null>(initialSettings);
  /** Bumped by the socket's `settings` message: the settings are read again without waiting for the poll. */
  const [settingsChanged, setSettingsChanged] = useState(0);
  const [link, setLink] = useState<BoardLinkState>(initialLinkState ?? (boardKey ? 'ok' : 'missing'));
  const uiLang: Lang = settings?.venue.default_lang ?? lang;
  const wantedLangs: readonly Lang[] = settings?.voice_langs ?? [lang];
  const wantedKey = wantedLangs.join(',');
  const t = UI[uiLang];
  const [board, setBoard] = useState<PublicBoard | null>(null);
  const [online, setOnline] = useState(true);
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const [started, setStarted] = useState(readStarted);
  const [sound, setSound] = useState<SoundState>(() => boardSound().state);
  /** The board languages this browser has a voice for; empty means the chime is the whole alert. */
  const [voiceLangs, setVoiceLangs] = useState<Lang[]>(() => boardSound().voiceLangs(wantedLangs));
  const seen = useRef<string[] | null>(null);
  const startedRef = useRef(started);
  const flashTimers = useRef(new Map<string, number>());
  const langsRef = useRef(wantedLangs);
  langsRef.current = wantedLangs;

  // A new link opened on this screen (the URL's key changed) runs again.
  useEffect(() => {
    setLink(boardKey ? 'ok' : 'missing');
  }, [pickupPointId, boardKey]);

  // Settings: read on load and every 30 s with the link's key, so a second language set on the dashboard reaches the
  // TV without a reload. Until the first answer the device language stands in; a failed poll keeps the last good
  // settings. A 401 means the link was revoked or is wrong: the key is forgotten and the board stops.
  const running = link === 'ok' && boardKey !== null;
  useEffect(() => {
    if (!running || !boardKey) return;
    let stopped = false;
    const load = () => {
      api<BoardSettings>(`/board/${encodeURIComponent(pickupPointId)}?${BOARD_LINK.param}=${encodeURIComponent(boardKey)}`)
        .then((s) => {
          if (stopped) return;
          setSettings(s);
          rememberBoardKey(pickupPointId, boardKey);
        })
        .catch((e: ApiError) => {
          if (stopped || e.status !== 401) return; // offline or a server error: the last settings stay
          rememberBoardKey(pickupPointId, null);
          setLink('invalid');
        });
    };
    load();
    const timer = window.setInterval(load, SETTINGS_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [pickupPointId, boardKey, running, settingsChanged]);

  // Which languages have a voice: re-checked when the languages change and whenever the engine reports a change.
  useEffect(() => {
    setVoiceLangs(boardSound().voiceLangs(langsRef.current));
  }, [wantedKey, sound]);

  // Live board, the public one: display numbers and states, no ticket ids (issue #4); a number is unique among the
  // open orders of the pickup point, so it keys the flash and the memory. The memory of ready numbers is restored
  // before the first snapshot and written on every snapshot, so a reload shows the numbers that were already ready
  // in silence and still announces a number that turned ready in between. A reconnect keeps the in-memory list, so
  // it never announces twice either.
  useEffect(() => {
    if (!running || !boardKey) return;
    const key = memoryKey(pickupPointId);
    const persist = () => {
      if (seen.current === null) return;
      try {
        localStorage.setItem(key, writeBoardMemory(seen.current, Date.now()));
      } catch {
        // storage unavailable: the baseline rule still applies
      }
    };
    try {
      seen.current = readBoardMemory(localStorage.getItem(key), Date.now());
    } catch {
      seen.current = null;
    }
    const timers = flashTimers.current;
    const unsubscribe = subscribeBoard<PublicBoard>(
      pickupPointId,
      'board',
      (b) => {
        const { fresh: newOnes, seen: nos } = announceReady(seen.current, b.ready);
        seen.current = nos;
        persist();
        setBoard(b);
        if (newOnes.length === 0) return;
        setFresh((prev) => new Set([...prev, ...newOnes.map((o) => o.displayNo)]));
        for (const o of newOnes) {
          window.clearTimeout(timers.get(o.displayNo));
          timers.set(
            o.displayNo,
            window.setTimeout(() => {
              timers.delete(o.displayNo);
              setFresh((prev) => {
                const next = new Set(prev);
                next.delete(o.displayNo);
                return next;
              });
            }, BOARD_FLASH_MS),
          );
        }
        const engine = boardSound();
        engine.chime();
        engine.callOut(
          newOnes.map((o) => o.displayNo),
          langsRef.current,
        );
      },
      setOnline,
      () => setSettingsChanged((n) => n + 1),
      {
        key: boardKey,
        onRevoked: () => {
          rememberBoardKey(pickupPointId, null);
          setLink('invalid');
        },
      },
    );
    window.addEventListener('pagehide', persist);
    return () => {
      unsubscribe();
      window.removeEventListener('pagehide', persist);
      for (const id of timers.values()) window.clearTimeout(id);
      timers.clear();
      seen.current = null;
      setOnline(true);
    };
  }, [pickupPointId, boardKey, running]);

  // Sound: any gesture on the page unlocks it (the remote's OK key counts), the page shown again resumes it,
  // and the first gesture also dismisses the start overlay for good.
  useEffect(() => {
    const engine = boardSound();
    const unsubscribe = engine.subscribe((s) => {
      setSound(s);
      setVoiceLangs(engine.voiceLangs(langsRef.current));
    });
    const onGesture = () => {
      // Only while locked: unlocking again would cut a call-out in progress.
      if (engine.state === 'locked') engine.unlock();
      if (!startedRef.current) {
        startedRef.current = true;
        try {
          localStorage.setItem(STARTED_KEY, '1');
        } catch {
          // storage unavailable: the overlay shows again on the next load
        }
        setStarted(true);
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') engine.resume();
    };
    window.addEventListener('click', onGesture);
    window.addEventListener('touchend', onGesture);
    window.addEventListener('keydown', onGesture);
    document.addEventListener('visibilitychange', onVisible);
    if (startedRef.current) engine.resume();
    return () => {
      unsubscribe();
      window.removeEventListener('click', onGesture);
      window.removeEventListener('touchend', onGesture);
      window.removeEventListener('keydown', onGesture);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (link !== 'ok') return <BoardLinkScreen lang={uiLang} state={link} />;

  return (
    <div className="board" lang={uiLang} dir={LANG_DIR[uiLang]} style={numberSize}>
      <header>
        <div>
          <h1>{t.tv_heading}</h1>
          <div className="muted">{t.tv_sub}</div>
        </div>
        <div className="board-side">
          {sound === 'on' ? (
            <>
              <span className="tag inverse" role="status">
                <SpeakerIcon /> {t.sound_on}
              </span>
              {voiceLangs.length > 0 ? (
                <span className="tag inverse" role="status">
                  <VoiceIcon /> {fill(t.tv_voice_on, { langs: voiceLangs.map((l) => LANG_LABELS[l]).join(' · ') })}
                </span>
              ) : (
                <span className="tag inverse" role="status">
                  <MutedIcon /> {t.tv_voice_off}
                </span>
              )}
            </>
          ) : null}
          {settings?.venue.logo_url ? (
            // The venue logo replaces the wordmark (issue #20); the off-white plate keeps a dark logo readable on the forest board.
            <span className="board-plate">
              <img className="board-logo" src={settings.venue.logo_url} alt={settings.venue.name} />
            </span>
          ) : (
            <Logo reverse className="board-logo" />
          )}
        </div>
      </header>
      {!online ? (
        <div className="banner" role="status">
          {t.offline}
        </div>
      ) : null}
      {started && sound === 'locked' ? (
        <button type="button" className="banner board-sound">
          <MutedIcon /> {t.tv_sound_off}
        </button>
      ) : null}
      <div className="cols">
        <section>
          <h2>
            <Icon name="clock" />
            {t.order_preparing}
          </h2>
          {board?.waiting.length === 0 ? <p className="board-empty">{t.empty}</p> : null}
          <div className="nums">
            {board?.waiting.map((o) => (
              <BoardNumber key={o.displayNo} o={o} />
            ))}
          </div>
        </section>
        <section aria-live="polite">
          <h2>
            <Icon name="check" />
            {t.order_ready}
          </h2>
          {board?.ready.length === 0 ? <p className="board-empty">{t.design_board_waiting}</p> : null}
          <div className="nums">
            {board?.ready.map((o) => (
              <BoardNumber key={o.displayNo} o={o} ready fresh={fresh.has(o.displayNo)} label={t.tv_new} />
            ))}
          </div>
        </section>
      </div>
      <footer className="board-footer">
        <span>{settings?.venue.name}</span>
        <span>{t.design_guest_footer}</span>
      </footer>
      {!started ? (
        <div className="board-start" role="dialog" aria-modal="true" aria-labelledby="board-start-title">
          <Logo reverse />
          <h1 id="board-start-title">{t.tv_heading}</h1>
          <p>{t.tv_start_hint}</p>
          <button type="button" className="btn ready" autoFocus>
            <BellIcon /> {t.tv_start}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the board shows instead of the numbers when it cannot run (issue #16): no key in the URL, or a key the API
 * refused or revoked. A symbol and a heading carry the state (never color alone); the body says where the current link
 * is. Nothing else is fetched or opened. Exported for the render test.
 */
export function BoardLinkScreen({ lang, state }: { lang: Lang; state: Exclude<BoardLinkState, 'ok'> }) {
  const t = UI[lang];
  return (
    <div className="board board-link-state" lang={lang} dir={LANG_DIR[lang]}>
      <div className="board-start" role="alert">
        <Logo reverse />
        <h1>
          <LinkOffIcon /> {state === 'missing' ? t.tv_link_missing_title : t.tv_link_invalid_title}
        </h1>
        <p>{state === 'missing' ? t.tv_link_missing_body : t.tv_link_invalid_body}</p>
      </div>
    </div>
  );
}

/** One number on the board, always an LTR run (never mirrored in Arabic); a fresh one carries the bell and the word. Exported for the render test. */
export function BoardNumber({ o, ready = false, fresh = false, label }: { o: PublicBoardEntry; ready?: boolean; fresh?: boolean; label?: string }) {
  return (
    <div className={`num${ready ? ' ready' : ''}${fresh ? ' fresh' : ''}`}>
      {fresh ? (
        <span className="new">
          <BellIcon /> {label}
        </span>
      ) : null}
      <bdi dir="ltr">{o.displayNo}</bdi>
    </div>
  );
}

// Inline icons: a TV browser may lack an emoji font, and status is never conveyed by color alone.
function BellIcon() {
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 2a1.5 1.5 0 0 1 1.5 1.5v.8A6 6 0 0 1 18 10v4.6l1.7 2.2a1 1 0 0 1-.8 1.6H5.1a1 1 0 0 1-.8-1.6L6 14.6V10a6 6 0 0 1 4.5-5.7v-.8A1.5 1.5 0 0 1 12 2zm-2.5 17.5h5a2.5 2.5 0 0 1-5 0z" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9h3.5L12 5v14l-4.5-4H4zM15 8.5a4 4 0 0 1 0 7v-2a2 2 0 0 0 0-3zm0-4a8 8 0 0 1 0 15v-2a6 6 0 0 0 0-11z" />
    </svg>
  );
}

function MutedIcon() {
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 9h3.5L12 5v14l-4.5-4H4zm11.2 0 1.8 1.8L18.8 9l1.4 1.4-1.8 1.8 1.8 1.8-1.4 1.4-1.8-1.8-1.8 1.8-1.4-1.4 1.8-1.8-1.8-1.8z" />
    </svg>
  );
}

/** A broken chain link: the board's link is missing or no longer valid. */
function LinkOffIcon() {
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3.3 2 2 3.3l4.2 4.2A5 5 0 0 0 7 17h3v-2H7a3 3 0 0 1-.3-6l2.1 2.1V13h2.2l8.7 8.7 1.3-1.3zM17 7h-3v2h3a3 3 0 0 1 1.4 5.7l1.4 1.4A5 5 0 0 0 17 7zm-3.2 4 2 2H16v-2z" />
    </svg>
  );
}

/** A speech bubble: the voice call-out, as opposed to the chime. */
function VoiceIcon() {
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-8l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm3 5v2h10V9zm0 4v2h6v-2z" />
    </svg>
  );
}
